import React, { useState, useEffect } from 'react';
import { Bot, X } from 'lucide-react';

const AUTONOMY_OPTIONS = [
  { value: 'observe', label: 'Observe Only', description: 'Monitors strategies and reports insights — takes no actions' },
  { value: 'suggest', label: 'Suggest', description: 'Proposes actions for your approval before executing' },
  { value: 'auto', label: 'Auto-Execute', description: 'Takes actions autonomously within configured guardrails' },
];

const selectClasses = 'w-full bg-card border border-white/10 rounded-lg px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary';
const inputClasses = 'w-full bg-card border border-white/10 rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary';

function BrokerSettingsModal({ open, onClose, onSave, broker }) {
  const isEdit = !!broker;
  const [name, setName] = useState('');
  const [autonomy, setAutonomy] = useState('observe');
  const [assetFocus, setAssetFocus] = useState('');
  const [tradingMode, setTradingMode] = useState('paper');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (broker) {
      setName(broker.name || '');
      setAutonomy(broker.autonomy || 'observe');
      setAssetFocus(broker.config?.assetFocus || '');
      setTradingMode(broker.config?.tradingMode || 'paper');
    } else {
      setName('');
      setAutonomy('observe');
      setAssetFocus('');
      setTradingMode('paper');
    }
    setError(null);
  }, [open, broker]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        autonomy,
        config: {
          assetFocus: assetFocus || undefined,
          tradingMode,
        },
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-white/10 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
              <Bot className="w-4 h-4 text-primary" />
            </div>
            <h2 className="text-lg font-bold text-foreground">
              {isEdit ? 'Broker Settings' : 'New Broker'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Broker Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., CryptoPaper, LiveEquities"
              className={inputClasses}
              required
            />
          </div>

          {/* Trading Mode */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Trading Mode</label>
            <div className="flex gap-3">
              {['paper', 'live'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setTradingMode(mode)}
                  className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                    tradingMode === mode
                      ? mode === 'live'
                        ? 'bg-red-500/15 border-red-500/30 text-red-400'
                        : 'bg-primary/15 border-primary/30 text-primary'
                      : 'border-white/10 text-muted-foreground hover:bg-accent/10'
                  }`}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Asset Focus */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Asset Focus (optional)</label>
            <select
              value={assetFocus}
              onChange={(e) => setAssetFocus(e.target.value)}
              className={selectClasses}
            >
              <option value="">Any</option>
              <option value="crypto">Crypto</option>
              <option value="equities">Equities</option>
            </select>
          </div>

          {/* Autonomy */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Autonomy Level</label>
            <div className="space-y-2">
              {AUTONOMY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAutonomy(opt.value)}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                    autonomy === opt.value
                      ? 'bg-primary/15 border-primary/30'
                      : 'border-white/10 hover:bg-accent/10'
                  }`}
                >
                  <p className={`text-sm font-medium ${autonomy === opt.value ? 'text-primary' : 'text-foreground'}`}>
                    {opt.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!name.trim() || saving}
            className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Broker'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default BrokerSettingsModal;
